import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import ClientQuickProfile from '../../components/common/ClientQuickProfile';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { cn, formatDate, formatDateTime, getShiftTypeClass } from '../../lib/utils';
import { 
  FileText, Calendar, User, Home, Filter, Search, 
  ChevronDown, ChevronUp, Clock, RefreshCw, Eye,
  Users, Utensils, Pill, Activity, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, endOfWeek } from 'date-fns';

const TeamShiftNotesPage = () => {
  const { api, user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState({});
  
  // Filters
  const [participantFilter, setParticipantFilter] = useState('all');
  const [shiftTypeFilter, setShiftTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('week');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Quick profile dialog
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const getDateRange = useCallback(() => {
    const today = new Date();
    switch (dateRange) {
      case 'today':
        return { start: format(today, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'week':
        return { 
          start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        };
      case 'month':
        return { 
          start: format(subDays(today, 30), 'yyyy-MM-dd'),
          end: format(today, 'yyyy-MM-dd')
        };
      case 'custom':
        return { start: startDate, end: endDate };
      default:
        return { start: '', end: '' };
    }
  }, [dateRange, startDate, endDate]);

  const fetchParticipants = useCallback(async () => {
    try {
      const response = await api.get('/shift-notes/team/participants');
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
    }
  }, [api]);

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      
      const params = new URLSearchParams();
      if (participantFilter !== 'all') {
        params.append('participant_id', participantFilter);
      }
      if (shiftTypeFilter !== 'all') {
        params.append('shift_type', shiftTypeFilter);
      }
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      params.append('limit', '100');
      
      const response = await api.get(`/shift-notes/team?${params.toString()}`);
      setNotes(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Team notes viewing is currently disabled');
      } else {
        toast.error('Failed to load team notes');
      }
    } finally {
      setLoading(false);
    }
  }, [api, participantFilter, shiftTypeFilter, getDateRange]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const toggleNoteExpanded = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const openQuickProfile = (participantId) => {
    setSelectedParticipant(participantId);
    setProfileDialogOpen(true);
  };

  const hasContent = (section) => {
    if (!section) return false;
    if (typeof section === 'string') return section.trim().length > 0;
    if (typeof section === 'object') {
      return Object.values(section).some(v => v && v.toString().trim().length > 0);
    }
    return false;
  };

  const renderNoteSection = (title, icon, content, bgColor = 'bg-muted/30') => {
    if (!hasContent(content)) return null;
    
    return (
      <div className={cn("rounded-lg p-3", bgColor)}>
        <h5 className="font-medium text-sm flex items-center gap-2 mb-2">
          {icon}
          {title}
        </h5>
        {typeof content === 'string' ? (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{content}</p>
        ) : (
          <div className="text-sm text-muted-foreground space-y-1">
            {Object.entries(content).map(([key, value]) => (
              value && (
                <div key={key}>
                  <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {value}
                </div>
              )
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team Shift Notes"
        description="View shift notes from your team members for shared participants"
        icon={<FileText className="h-6 w-6" />}
      />

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Participant Filter */}
            <div className="space-y-2">
              <Label>Participant</Label>
              <Select value={participantFilter} onValueChange={setParticipantFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Participants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Participants</SelectItem>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.house_name && `(${p.house_name})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Shift Type Filter */}
            <div className="space-y-2">
              <Label>Shift Type</Label>
              <Select value={shiftTypeFilter} onValueChange={setShiftTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="AM">AM Shift</SelectItem>
                  <SelectItem value="PM">PM Shift</SelectItem>
                  <SelectItem value="Active Night">Active Night</SelectItem>
                  <SelectItem value="Sleepover">Sleepover</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label>Date Range</Label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={fetchNotes}
                disabled={loading}
                className="w-full"
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-muted-foreground">Loading team notes...</p>
            </CardContent>
          </Card>
        ) : notes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="font-semibold mb-2">No Notes Found</h3>
              <p className="text-muted-foreground text-sm">
                {participantFilter !== 'all' || shiftTypeFilter !== 'all' || dateRange !== 'week'
                  ? "Try adjusting your filters to see more notes"
                  : "No team shift notes available for your assigned participants"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Showing {notes.length} note{notes.length !== 1 ? 's' : ''}</span>
              <span>Read-only view</span>
            </div>

            {notes.map(note => (
              <Card key={note.id} className="overflow-hidden">
                <Collapsible 
                  open={expandedNotes[note.id]} 
                  onOpenChange={() => toggleNoteExpanded(note.id)}
                >
                  {/* Note Header - Always visible */}
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge className={getShiftTypeClass(note.shift_type)}>
                              {note.shift_type}
                            </Badge>
                            <span className="font-semibold">{note.participant_name}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(note.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {note.worker_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Home className="h-3 w-3" />
                              {note.house_name}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openQuickProfile(note.participant_id);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Quick Profile
                          </Button>
                          {expandedNotes[note.id] ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  {/* Note Content - Expandable */}
                  <CollapsibleContent>
                    <CardContent className="pt-0 space-y-3">
                      {/* ADLs */}
                      {renderNoteSection(
                        "Activities of Daily Living", 
                        <Users className="h-4 w-4" />, 
                        note.adls,
                        "bg-blue-50 dark:bg-blue-950/30"
                      )}

                      {/* Meals */}
                      {renderNoteSection(
                        "Meals & Nutrition", 
                        <Utensils className="h-4 w-4" />, 
                        note.meals,
                        "bg-green-50 dark:bg-green-950/30"
                      )}

                      {/* Medications */}
                      {renderNoteSection(
                        "Medications", 
                        <Pill className="h-4 w-4" />, 
                        note.medications,
                        "bg-purple-50 dark:bg-purple-950/30"
                      )}

                      {/* Behaviours */}
                      {renderNoteSection(
                        "Behaviours", 
                        <Activity className="h-4 w-4" />, 
                        note.behaviours,
                        "bg-amber-50 dark:bg-amber-950/30"
                      )}

                      {/* Community Access */}
                      {renderNoteSection(
                        "Community Access", 
                        <Home className="h-4 w-4" />, 
                        note.community_access
                      )}

                      {/* Goals Progress */}
                      {renderNoteSection(
                        "Goals Progress", 
                        <Activity className="h-4 w-4" />, 
                        note.goals_progress
                      )}

                      {/* Incidents/Near Misses */}
                      {hasContent(note.incidents_near_misses) && (
                        <div className="rounded-lg p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
                          <h5 className="font-medium text-sm flex items-center gap-2 mb-2 text-red-700 dark:text-red-400">
                            <Activity className="h-4 w-4" />
                            Incidents / Near Misses
                          </h5>
                          <p className="text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">
                            {note.incidents_near_misses}
                          </p>
                        </div>
                      )}

                      {/* General Notes */}
                      {renderNoteSection(
                        "General Notes", 
                        <MessageSquare className="h-4 w-4" />, 
                        note.general_notes
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
                        <span>Created: {formatDateTime(note.created_at)}</span>
                        <Badge variant="outline">Read-only</Badge>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </>
        )}
      </div>

      {/* Quick Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Client Quick Profile</DialogTitle>
          </DialogHeader>
          {selectedParticipant && (
            <ClientQuickProfile 
              participantId={selectedParticipant}
              variant="expanded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamShiftNotesPage;
